import React, { useState } from "react";
import { toastr } from 'react-redux-toastr';
import { Card, CardHeader, CardBody, Row, Col, Button } from "reactstrap";

// Re-runs an incentive report for the latest closed cycle and downloads the CSV.
// Backend: POST csp/incentive_report_download (CS admins only). Same code as the month-end email, no email or upload.
// The backend picks the cycle; the dates below are only shown so you know which cycle you get.

const IST_OFFSET_MS = 330 * 60 * 1000;

const REPORTS = [
	{ key: 'bd', label: 'BD incentive', hint: 'Payments in the cycle, per BD rep' },
	{ key: 'cs_monthly', label: 'CS monthly NRR', hint: 'CS reps, ARR / 12' },
	{ key: 'pe_monthly', label: 'PE panel monthly NRR', hint: 'Production engineers, panel ARR / 12' },
	{ key: 'cs_quarterly', label: 'CS quarterly NRR', hint: 'Orgs with 4-19 licences, ARR / 8' },
	{ key: 'cs_quarterly_all', label: 'CS quarterly NRR (all orgs)', hint: 'Every org, ARR / 8. Not in the month-end email' },
];

// Same cycle rules as backend cron_job_scripts/incentive_cycle_utils.js and controllers/csp/incentive_report_download.js.
// Last Monday of the month before `month`, as a UTC calendar date.
const last_monday_before_month = (month, year) => {
	const date = new Date(Date.UTC(year, month, 0));
	while (date.getUTCDay() !== 1) date.setUTCDate(date.getUTCDate() - 1);
	return date;
};
const months_back = ({ month, year }, n) => {
	const total = year * 12 + month - n;
	return { month: total % 12, year: Math.floor(total / 12) };
};

// Monthly: the cycle closing in the current IST month once it has ended (00:00 IST on its last Monday), else last month's.
// Quarterly: the latest closed cycle in March, June, September or December, from the start of its quarter.
const latest_closed_cycles = () => {
	const ist_now = new Date(Date.now() + IST_OFFSET_MS);
	let latest = { month: ist_now.getUTCMonth(), year: ist_now.getUTCFullYear() };
	if (Date.now() < last_monday_before_month(latest.month + 1, latest.year).getTime() - IST_OFFSET_MS) latest = months_back(latest, 1);
	let quarter = latest;
	while (quarter.month % 3 !== 2) quarter = months_back(quarter, 1);
	return {
		monthly: { start: last_monday_before_month(latest.month, latest.year), end: last_monday_before_month(latest.month + 1, latest.year) },
		quarterly: { start: last_monday_before_month(quarter.month - 2, quarter.year), end: last_monday_before_month(quarter.month + 1, quarter.year) },
	};
};

const format_day = (date) => date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const save_blob = (blob, filename) => {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};

const IncentiveReports = () => {
	const [running_report, set_running_report] = useState(null);
	const cycles = latest_closed_cycles();

	const download_report = async (report) => {
		set_running_report(report.key);
		try {
			const response = await fetch(global.config.server_path + 'csp/incentive_report_download', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ report: report.key }),
			});
			const content_type = response.headers.get('Content-Type') || '';
			if (response.ok && content_type.includes('text/csv')) {
				const disposition = response.headers.get('Content-Disposition') || '';
				const match = disposition.match(/filename="([^"]+)"/);
				save_blob(await response.blob(), match ? match[1] : `${report.key}_incentive.csv`);
				toastr.success(`${report.label} downloaded`);
			} else {
				// Errors come back as JSON { message }; a gateway timeout comes back as HTML.
				let message = `Request failed (HTTP ${response.status})`;
				try {
					const body = await response.json();
					if (body && body.message) message = body.message;
				} catch (err) { /* not JSON */ }
				toastr.error(`${report.label} failed`, message);
			}
		} catch (err) {
			console.error(err);
			toastr.error(`${report.label} failed`, err.message || 'Network error');
		} finally {
			set_running_report(null);
		}
	};

	return (
		<Card className="shadow mb-4">
			<CardHeader className="border-0">
				<h3 className="mb-1">Incentive reports</h3>
				<div style={{ fontSize: '13px', color: '#666' }}>
					Re-run an incentive report for the latest closed cycle and download its CSV. It uses the same code as the
					month-end email, but sends no email and saves nothing. Each report can take a minute or two.
				</div>
			</CardHeader>
			<CardBody className="pt-0">
				<div style={{ fontSize: '13px' }}>
					<div>BD, CS monthly and PE panel: <b>{format_day(cycles.monthly.start)}</b> to <b>{format_day(cycles.monthly.end)}</b></div>
					<div>CS quarterly (both): <b>{format_day(cycles.quarterly.start)}</b> to <b>{format_day(cycles.quarterly.end)}</b></div>
				</div>
				<Row className="mt-3">
					{REPORTS.map(report => (
						<Col xs="auto" key={report.key} className="mb-2">
							<Button color="primary" size="sm" disabled={!!running_report} onClick={() => download_report(report)}>
								{running_report === report.key ? <i className="fa fa-spinner fa-spin mr-1" /> : <i className="fa fa-download mr-1" />}
								{report.label}
							</Button>
							<div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{report.hint}</div>
						</Col>
					))}
				</Row>
			</CardBody>
		</Card>
	);
};

export default IncentiveReports;
